import { useEffect, useMemo, useRef, useState } from "react"
import { useShallow } from "zustand/react/shallow"

import type { PaymentOption } from "@be-miniapp/api"
import { getFareDrawerLayout } from "@be-miniapp/ui/pages/ride/fare/hooks/fare-drawer-layout"
import { useMapBounds } from "@be-miniapp/ui/pages/ride/fare/hooks/use-map-bounds"

import type { CurrentRideData } from "../stores/ride-data"
import {
  selectRideBooking,
  selectRideBookingActions,
  selectRidePickupDropoff,
} from "../stores/ride-data.selectors"
import { calculateDistanceFromTwoPoints } from "./map"
import {
  resolveFarePaymentMethod,
  resolvePreferredPaymentMode,
} from "./payment"

/** Zustand `useStore`-style hook bound to `CurrentRideData` (each app’s `useRideDataStore`). */
export type RideDataStoreHook = <T>(
  selector: (state: CurrentRideData) => T,
  equalityFn?: (a: T, b: T) => boolean
) => T

export interface UseFarePageModelDeps {
  useRideDataStore: RideDataStoreHook
  usePaymentMethod: (params: {
    paymentMethodCode: number | undefined
  }) => PaymentOption | undefined
  partnerDefaultPaymentMethod?: number
}

/**
 * Shared fare screen composition: route + booking slice, long-trip flag, resolved payment fallback,
 * and map fit lifecycle. Platform-specific layout (shell, z-index) stays in each app’s `fare.tsx`.
 */
export function useFarePageModel(deps: UseFarePageModelDeps) {
  const { useRideDataStore, usePaymentMethod, partnerDefaultPaymentMethod } =
    deps

  const { pickup, dropoff } = useRideDataStore(
    useShallow(selectRidePickupDropoff)
  )
  const {
    vehicleType,
    paymentMethod: paymentOption,
    preferredPaymentMethod,
  } = useRideDataStore(useShallow(selectRideBooking))
  const { setVehicleType } = useRideDataStore(
    useShallow(selectRideBookingActions)
  )

  const mapRef = useRef<any | null>(null)

  const pickupLatLng = useMemo(
    () => ({
      lat: pickup?.location?.latitude ?? 0,
      lng: pickup?.location?.longitude ?? 0,
    }),
    [pickup?.location?.latitude, pickup?.location?.longitude]
  )

  const dropoffLatLng = useMemo(
    () => ({
      lat: dropoff?.location?.latitude ?? 0,
      lng: dropoff?.location?.longitude ?? 0,
    }),
    [dropoff?.location?.latitude, dropoff?.location?.longitude]
  )

  const isLongTrip = useMemo(() => {
    if (!pickup?.location || !dropoff?.location) return false
    const distance = calculateDistanceFromTwoPoints(
      pickup.location,
      dropoff.location
    )
    return distance > 30000
  }, [pickup?.location, dropoff?.location])

  const preferPaymentMode = resolvePreferredPaymentMode({
    paymentMethod: paymentOption,
    preferredPaymentMethod,
    partnerDefaultPaymentMethod,
  })

  const paymentLookupCode =
    paymentOption?.payment_method ?? preferPaymentMode

  const enrichedPaymentMethod = usePaymentMethod({
    paymentMethodCode: paymentLookupCode,
  })

  const paymentMethod = resolveFarePaymentMethod({
    storePaymentMethod: paymentOption,
    enrichedPaymentMethod,
  })

  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight)
  const drawerLayout = useMemo(
    () => getFareDrawerLayout(viewportHeight),
    [viewportHeight]
  )
  const height = drawerLayout.collapsedHeight
  const mapOffset = drawerLayout.collapsedHeight

  const { mapReady, setMapReady, tryFitBound } = useMapBounds({
    pickupLatLng,
    dropoffLatLng,
    mapRef,
    bottomOffset: mapOffset,
  })

  useEffect(() => {
    const onResize = () => {
      setViewportHeight(window.innerHeight)
    }

    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (mapReady) {
      timeoutId = setTimeout(() => {
        tryFitBound()
      }, 200)
    }
    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
      }
    }
  }, [mapReady, tryFitBound])

  return {
    pickup,
    dropoff,
    vehicleType,
    setVehicleType,
    isLongTrip,
    paymentMethod,
    mapRef,
    setMapReady,
    tryFitBound,
    height,
    mapOffset,
  }
}
